import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useToast,
  Text,
  Flex,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  VStack,
  HStack,
  SimpleGrid,
  useBreakpointValue,
} from "components-lib";
import { API_ENDPOINTS } from "config/api";
import {
  fetchParametersByDoctor,
  fetchParametersByAppointment,
  saveAppointmentParameters
} from 'services/parametersService';

const ParametersModal = ({
  isOpen,
  onClose,
  patientData,
  onChange,
  doctor_id,
}) => {
  console.log("ParametersModal rendered with props:", {
    isOpen,
    hasOnClose: !!onClose,
    patientData,
    hasOnChange: !!onChange,
  });
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [parameterDefinitions, setParameterDefinitions] = useState([]);
  const [parameterValues, setParameterValues] = useState({});
  const [existingParameters, setExistingParameters] = useState([]);

  const isMobile = useBreakpointValue({ base: true, md: false });

  // Fetch parameter definitions and existing values when modal opens
  const fetchParameters = useCallback(async () => {
    // Try to get doctor_id from different possible locations in the data
    const doctorId = patientData?.doctor_id || doctor_id ||
      patientData?.doctor?.id ||
      patientData?.doctor_id ||
      (patientData.doctor && typeof patientData.doctor === 'object' ?
        patientData.doctor.id || patientData.doctor.doctor_id : null) ||
      null;


    console.log("fetchParameters called with:", {
      isOpen,
      patientData,
      doctorId,
      hasAppointmentId: !!(patientData?.id || patientData?.appointment_id),
      allPatientDataKeys: patientData ? Object.keys(patientData) : [],
    });

    if (!isOpen || !doctorId) {
      console.error("Skipping fetch - missing required data:", {
        isOpen,
        doctorId,
        patientData: patientData
          ? {
            ...patientData,
            // Include doctor object keys if it exists
            doctor: patientData.doctor
              ? Object.keys(patientData.doctor)
              : "No doctor data",
          }
          : "No patient data",
      });
      if (isOpen) {
        toast({
          title: "Error",
          description: "Doctor information is missing. Cannot load parameters.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    setIsLoading(true);
    setError(null);
    setTimeout(async () => {
      try {
        // Fetch parameter definitions for the doctor
        const definitions = await fetchParametersByDoctor(doctorId);
        console.log("Definitions:", definitions);
        setParameterDefinitions(definitions);

        // Initialize empty values for all parameters
        const initialValues = {};
        definitions.forEach((param) => {
          initialValues[param.id] = "";
        });
        setParameterValues(initialValues);

        // Fetch existing parameter values for this appointment
        if (patientData.id || patientData.appointment_id) {
          const apptId = patientData.id || patientData.appointment_id;
          const existing = await fetchParametersByAppointment(apptId);
          setExistingParameters(existing);

          // Update values with existing data
          const updatedValues = { ...initialValues };
          existing.forEach((param) => {
            if (param.parameter_id in updatedValues) {
              updatedValues[param.parameter_id] = param.value;
            }
          });
          setParameterValues(updatedValues);
        }
      } catch (error) {
        console.error("Error fetching parameters:", error);
        setError(error.message || "Failed to load parameters");

        toast({
          title: "Error",
          description: "Failed to load parameters. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    }, 1000);

  }, [isOpen, patientData, toast]);

  // Reset and fetch data when modal opens
  useEffect(() => {
    console.log("useEffect triggered", {
      isOpen,
      hasPatientData: !!patientData,
    });
    if (isOpen) {
      console.log("Modal is open, fetching parameters...");
      fetchParameters().catch((error) => {
        console.error("Error in fetchParameters:", error);
      });
    }
  }, [isOpen, fetchParameters, patientData]);

  // Handle parameter value changes
  const handleParameterChange = (parameterId, value) => {
    setParameterValues((prev) => ({
      ...prev,
      [parameterId]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!patientData?.id && !patientData?.appointment_id) return;

    setIsSaving(true);
    setError(null);

    try {
      // Prepare parameters array in the required format
      const parametersToSave = Object.entries(parameterValues)
        .filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
        .map(([parameter_id, value]) => ({
          parameter_id: parseInt(parameter_id, 10),
          value: String(value).trim(),
        }));

      if (parametersToSave.length === 0) {
        throw new Error("No parameters to save");
      }

      // Save parameters
      const appointmentId = patientData.id || patientData.appointment_id;
      await saveAppointmentParameters(appointmentId, parametersToSave);

      // Show success message
      toast({
        title: "Success",
        description: "Parameters saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Notify parent component
      if (onChange) {
        onChange(parametersToSave);
      }

      // Close the modal
      onClose();
    } catch (error) {
      console.error("Error saving parameters:", error);
      setError(
        error.response?.data?.message ||
        error.message ||
        "Failed to save parameters"
      );

      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to save parameters",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Group parameters into rows with 8 columns each
  const groupParametersIntoRows = (params) => {
    const rows = [];
    const itemsPerRow = 8;

    for (let i = 0; i < params.length; i += itemsPerRow) {
      const row = params.slice(i, i + itemsPerRow);
      rows.push(row);
    }

    return rows;
  };

  // Classify parameter side (right/left/other) based on name/aliases
  const classifyParamSide = (param) => {
    if (!param || !param.name) return "other";
    const raw = String(param.name || "");
    const lower = raw.toLowerCase();

    // Quick direct patterns (handle O.D., O.S., OD, OS, oculus dexter/sinister)
    if (/\bo\.?d\.?\b/i.test(raw) || /oculus\s*dexter/i.test(raw)) return "right";
    if (/\bo\.?s\.?\b/i.test(raw) || /oculus\s*sinister/i.test(raw)) return "left";

    // Normalize: remove dots and replace non-alphanum with spaces, then split tokens
    const cleaned = lower.replace(/\./g, "").replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
    const tokens = cleaned.split(" ").filter(Boolean);

    // If explicit words present, prefer them
    if (tokens.includes("right") || tokens.includes("righ")) return "right";
    if (tokens.includes("left")) return "left";

    // Common short abbreviations: RE/LE (Right Eye / Left Eye) - treat only when used as tokens
    if (tokens.includes("re") && tokens.includes("eye") ) return "right";
    if (tokens.includes("le") && tokens.includes("eye") ) return "left";

    // Single-letter tokens 'r'/'l' can be ambiguous — only use when there's an eye-related context
    const eyeContext = tokens.some(t => ["eye","vision","iop","sph","cyl","axis","near","far","va","oculus","add","prism"].includes(t));
    if (eyeContext) {
      if (tokens.includes("r") || tokens.includes("r.")) return "right";
      if (tokens.includes("l") || tokens.includes("l.")) return "left";
    }

    // As a fallback, if the phrase contains 'eye' and also 'right'/'left' words anywhere
    if (/\beye\b/.test(cleaned) && /\bright\b/.test(cleaned)) return "right";
    if (/\beye\b/.test(cleaned) && /\bleft\b/.test(cleaned)) return "left";

    return "other";
  };

  // Split parameters into Right / Left / Other groups based on API names
  const splitParametersBySide = (params) => {
    const right = [];
    const left = [];
    const other = [];

    (params || []).forEach((p) => {
      const side = classifyParamSide(p);
      if (side === "right") right.push(p);
      else if (side === "left") left.push(p);
      else other.push(p);
    });

    return { right, left, other };
  };

  // Render a single parameter cell
  const renderParameterCell = (param) => {
    const value = parameterValues[param.id] || "";
    const existingParam = existingParameters.find(
      (p) => p.parameter_id === param.id
    );
    const hasValue = value !== "" && value !== null && value !== undefined;

    return (
      <Box
        key={param.id}
        p={2}
        borderWidth="1px"
        borderRadius="md"
        borderColor="gray.200"
        bg="white"
        _hover={{ bg: "gray.50" }}
        h="100%"
        display="flex"
        flexDirection="column"
        minWidth="120px"
      >
        <FormControl
          isRequired={false}
          flex="1"
          display="flex"
          flexDirection="column"
          minH="110px"
        >
          <Box
            minH="40px"
            display="flex"
            alignItems="center"
            wordBreak="break-word"
          >
            <FormLabel
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              mb={1}
              whiteSpace="normal"
              overflow="hidden"
              textOverflow="ellipsis"
              title={param.name}
              flexShrink={1}
              lineHeight="short"
              m={0}
              p={0}
              width="100%"
            >
              <Box as="span" display="inline" wordBreak="break-word">
                {param.name}
              </Box>
            </FormLabel>
          </Box>
          <Box mt={2} mb={1} flexShrink={0}>
            <InputGroup size="sm">
              <Input
                type={param.unit === "date" ? "date" : "text"}
                placeholder="Value"
                value={value}
                onChange={(e) =>
                  handleParameterChange(param.id, e.target.value)
                }
                size="sm"
                fontSize="sm"
                height="32px"
                focusBorderColor="blue.500"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 1px #3182ce",
                }}
                pr={param.unit ? "3.5rem" : undefined}
              />
              {param.unit && (
                <InputRightElement width="3.5rem" pr={2}>
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                    {param.unit}
                  </Text>
                </InputRightElement>
              )}
            </InputGroup>
          </Box>
          <Box mt="auto" minH="20px" flexShrink={0}>
            {existingParam && !hasValue && (
              <Text
                fontSize="2xs"
                color="gray.500"
                noOfLines={1}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title={`Prev: ${existingParam.value} ${param.unit || ""}`}
              >
                Prev: {existingParam.value} {param.unit}
              </Text>
            )}
          </Box>
        </FormControl>
      </Box>
    );
  };

  // Render a row of parameters
  const renderParameterRow = (rowParams, rowIndex, allRows) => {
    const isLastRow = rowIndex === allRows.length - 1;
    const isSingleInLastRow = isLastRow && rowParams.length === 1;
    const columns = Math.min(8, rowParams.length); // Maximum 8 columns

    return (
      <Flex
        key={`row-${rowIndex}`}
        width="100%"
        justifyContent={isSingleInLastRow ? "center" : "flex-start"}
        mb={2}
      >
        <Box
          display="grid"
          gridTemplateColumns={`repeat(${columns}, minmax(120px, 1fr))`}
          gap="8px"
          width={isSingleInLastRow ? "auto" : "100%"}
        >
          {rowParams.map((param) => (
            <Box key={param.id} width="100%" display="flex">
              {renderParameterCell(param)}
            </Box>
          ))}
        </Box>
      </Flex>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={!isLoading && !isSaving ? onClose : undefined}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={handleSubmit}
        maxH="90vh"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader py={3} px={6} >
          <Text fontSize="s" fontWeight="semibold">
            Patient Parameters
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading || isSaving} />

        <ModalBody p={0} overflowY="auto" flex="1">
          <Box p={4}>
            {isLoading ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" />
              </Flex>
            ) : error ? (
              <Alert status="error" mb={4}>
                <AlertIcon />
                <Box>
                  <AlertTitle>Error loading parameters</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Box>
              </Alert>
            ) : parameterDefinitions.length === 0 ? (
              <Text>No parameters defined for this doctor.</Text>
            ) : (
              <Box width="100%" overflowX="hidden">
                {(() => {
                  const { right, left, other } = splitParametersBySide(parameterDefinitions || []);

                  const renderGroup = (title, params) => {
                    if (!params || params.length === 0) return null;
                    return (
                      <Box key={title} mb={4}>
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>{title}</Text>
                        {isMobile ? (
                          <VStack spacing={3} align="stretch">
                            {params.map((p) => renderParameterCell(p))}
                          </VStack>
                        ) : (
                          <Box width="100%" overflowX="auto">
                            <Box minW="min-content">
                              {groupParametersIntoRows(params).map((row, index, allRows) =>
                                renderParameterRow(row, index, allRows)
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  };

                  return (
                    <Box>
                      {renderGroup("Right Eye", right)}
                      {renderGroup("Left Eye", left)}
                      {renderGroup("Parameters", other)}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Box>
        </ModalBody>

        <ModalFooter p={4} >
          <HStack spacing={3}>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSaving}
              loadingText="Saving..."
              isDisabled={isLoading || parameterDefinitions.length === 0}
            >
              Save Parameters
            </Button>
            <Button
              onClick={onClose}
              isDisabled={isLoading || isSaving}
              variant="outline"
            >
              Cancel
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ParametersModal;
